import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import Modal from './Modal';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { fetchBranches } from '../branchesApi';
import { fetchBoards } from '../boardsApi';
import { fetchCommittees } from '../committeesApi';
import { createMeeting, fetchMeetingById, fetchMeetingParticipantOptions, mapMeetingFormToPayload, updateMeeting } from '../meetingsApi';
import { openGoogleCalendar } from '../calendarUtils';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './DecisionsPage.css';
import './DecisionFormPage.css';

const ICON_SEC = '#0EA5A0';

const MEETING_MEMBER_ROLES = ['أمين السر', 'عضو', 'رئيس'];

function emptyMeetingMemberForm() {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: 'عضو تنفيذي',
    memberType: 'عضو تنفيذي',
    role: 'عضو',
  };
}

export default function MeetingsFormPage() {
  const { language, direction } = useLanguage();
  const isRtl = language === 'ar' || language === 'ur'; // kept only for fillSampleData content
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: idParam } = useParams();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const isEdit = location.pathname.includes('/edit');
  const editId = isEdit ? Number(idParam) : null;

  // Computed steps array (re-derived on each render — cheap, no memo needed)
  const steps = [t('meetings.step0'), t('meetings.step1'), t('meetings.step2')];

  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);
  const [branches, setBranches] = useState([]);
  const [boards, setBoards] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [participantOptions, setParticipantOptions] = useState([]);
  const [members, setMembers] = useState([]);
  const [usersOpen, setUsersOpen] = useState(false);
  const [pickedUserKeys, setPickedUserKeys] = useState([]);
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [memberModalMode, setMemberModalMode] = useState('add');
  const [memberModalMemberId, setMemberModalMemberId] = useState(null);
  const [memberModalSource, setMemberModalSource] = useState('manual');
  const [memberModalForm, setMemberModalForm] = useState(() => emptyMeetingMemberForm());
  const [memberModalErrors, setMemberModalErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [form, setForm] = useState({
    companyId: '',
    name: '',
    goal: '',
    attendanceType: '',
    location: '',
    date: '',
    startTime: '',
    endTime: '',
    linkBoard: false,
    linkCommittee: false,
    boardId: '',
    committeeId: '',
    meetingLink: '',
    isOpen: false,
  });
  const [errors, setErrors] = useState({});

  const splitDateTime = (iso) => {
    if (!iso) return { date: '', time: '' };
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { date: '', time: '' };
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${min}` };
  };

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setAccessDenied(true); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const [branchRows, committeeRows] = await Promise.all([fetchBranches(), fetchCommittees()]);
        if (cancelled) return;
        const firstCompany = branchRows[0]?.id ? String(branchRows[0].id) : '';
        setBranches(branchRows);
        setBoards([]);
        setCommittees(committeeRows);
        if (!isEdit) {
          setForm((p) => ({ ...p, companyId: firstCompany }));
        } else if (editId && Number.isFinite(editId)) {
          const row = await fetchMeetingById(editId);
          if (cancelled) return;
          const start = splitDateTime(row.startAtUtc);
          const end = splitDateTime(row.endAtUtc);
          const companyId = String(row.companyId || firstCompany || '');
          const boardsRows = companyId ? await fetchBoards(companyId) : [];
          if (cancelled) return;
          setBoards(boardsRows);
          setForm({
            companyId,
            name: row.title || '',
            goal: row.goal || '',
            attendanceType: row.attendanceType || '',
            location: row.location || '',
            date: start.date,
            startTime: start.time,
            endTime: end.time,
            linkBoard: !!row.boardId,
            linkCommittee: !!row.committeeId,
            boardId: row.boardId ? String(row.boardId) : '',
            committeeId: row.committeeId ? String(row.committeeId) : '',
            meetingLink: row.zoomLink || '',
            isOpen: !!row.isOpen,
          });
          const prefilledMembers = (Array.isArray(row.invitees) ? row.invitees : []).map((x) => {
            const full = String(x.displayName || '').trim();
            const parts = full.split(/\s+/);
            if (x.targetType === 'email') {
              return {
                id: `m-email-${x.targetId}`,
                source: 'manual',
                participantKey: '',
                firstName: parts[0] || full || 'عضو',
                lastName: parts.slice(1).join(' '),
                email: x.targetId,
                phone: '',
                position: 'عضو',
                memberType: 'عضو',
                role: 'عضو',
              };
            }
            return {
              id: `u-${x.targetType}:${x.targetId}`,
              source: 'existing',
              participantKey: `${x.targetType}:${x.targetId}`,
              firstName: parts[0] || full || 'عضو',
              lastName: parts.slice(1).join(' '),
              email: '',
              phone: '',
              position: 'عضو تنفيذي',
              memberType: 'عضو تنفيذي',
              role: 'عضو',
            };
          });
          setMembers(prefilledMembers);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('meetings.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, isEdit, editId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!form.companyId) { setBoards([]); return; }
      try {
        const rows = await fetchBoards(form.companyId);
        if (!cancelled) setBoards(rows);
      } catch {
        if (!cancelled) setBoards([]);
      }
    })();
    return () => { cancelled = true; };
  }, [form.companyId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!form.companyId) { setParticipantOptions([]); return; }
      try {
        const rows = await fetchMeetingParticipantOptions(form.companyId);
        if (!cancelled) setParticipantOptions(rows);
      } catch {
        if (!cancelled) setParticipantOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [form.companyId]);

  const filteredBoards = useMemo(
    () => boards.filter((b) => String(b.companyId) === String(form.companyId) && b.isActive !== false),
    [boards, form.companyId],
  );
  const filteredCommittees = useMemo(
    () => committees.filter((c) => String(c.branchId) === String(form.companyId) && c.isActive !== false),
    [committees, form.companyId],
  );

  const validate = () => {
    const e = {};
    const req = t('meetings.required');
    if (!form.name.trim()) e.name = req;
    if (!form.goal.trim()) e.goal = req;
    if (!form.attendanceType) e.attendanceType = req;
    if (!form.location.trim()) e.location = req;
    if (!form.date) e.date = req;
    if (!form.startTime) e.startTime = req;
    if (!form.endTime) e.endTime = req;
    if (form.linkBoard && !form.boardId) e.boardId = req;
    if (form.linkCommittee && !form.committeeId) e.committeeId = req;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onStepClick = (targetStep) => {
    if (targetStep === step) return;
    setSaveMsg('');
    if (targetStep < step) { setStep(targetStep); return; }
    let current = step;
    while (current < targetStep) {
      if (current === 0 && !validate()) return;
      current += 1;
    }
    setStep(targetStep);
  };

  const closeMemberModal = () => {
    setMemberModalOpen(false);
    setMemberModalErrors({});
    setMemberModalMemberId(null);
  };

  const openMemberModalAdd = () => {
    setMemberModalMode('add');
    setMemberModalMemberId(null);
    setMemberModalSource('manual');
    setMemberModalForm(emptyMeetingMemberForm());
    setMemberModalErrors({});
    setMemberModalOpen(true);
  };

  const openMemberModalEdit = (m) => {
    setMemberModalMode('edit');
    setMemberModalMemberId(m.id);
    setMemberModalSource(m.source || 'manual');
    setMemberModalForm({
      firstName: m.firstName || '',
      lastName: m.lastName || '',
      email: m.email || '',
      phone: m.phone || '',
      position: m.position || 'عضو تنفيذي',
      memberType: m.memberType || 'عضو تنفيذي',
      role: m.role || 'عضو',
    });
    setMemberModalErrors({});
    setMemberModalOpen(true);
  };

  const openMemberModalDetail = (m) => {
    setMemberModalMode('detail');
    setMemberModalMemberId(m.id);
    setMemberModalSource(m.source || 'manual');
    setMemberModalForm({
      firstName: m.firstName || '',
      lastName: m.lastName || '',
      email: m.email || '',
      phone: m.phone || '',
      position: m.position || 'عضو تنفيذي',
      memberType: m.memberType || 'عضو تنفيذي',
      role: m.role || 'عضو',
    });
    setMemberModalErrors({});
    setMemberModalOpen(true);
  };

  const validateMeetingMemberModal = () => {
    const e = {};
    const req = t('meetings.required');
    if (!String(memberModalForm.firstName || '').trim()) e.firstName = req;
    if (!String(memberModalForm.lastName || '').trim()) e.lastName = req;
    setMemberModalErrors(e);
    return Object.keys(e).length === 0;
  };

  const saveMeetingMemberModal = () => {
    if (memberModalMode === 'detail') return;
    if (!validateMeetingMemberModal()) return;
    if (memberModalMode === 'add') {
      setMembers((p) => [
        ...p,
        {
          id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          source: 'manual',
          firstName: memberModalForm.firstName.trim(),
          lastName: memberModalForm.lastName.trim(),
          email: memberModalForm.email.trim(),
          phone: memberModalForm.phone.trim(),
          position: memberModalForm.position,
          memberType: memberModalForm.memberType,
          role: memberModalForm.role,
        },
      ]);
      closeMemberModal();
      return;
    }
    if (memberModalMode === 'edit' && memberModalMemberId) {
      updateMemberLocal(memberModalMemberId, {
        firstName: memberModalForm.firstName.trim(),
        lastName: memberModalForm.lastName.trim(),
        email: memberModalForm.email.trim(),
        phone: memberModalForm.phone.trim(),
        position: memberModalForm.position,
        memberType: memberModalForm.memberType,
        role: memberModalForm.role,
      });
      closeMemberModal();
    }
  };

  const updateMemberLocal = (id, patch) => setMembers((p) => p.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const removeMember = (id) => setMembers((p) => p.filter((m) => m.id !== id));
  const optionKey = (x) => `${x.targetType}:${x.targetId}`;
  const openUsersPicker = () => {
    const existing = new Set(members.filter((m) => m.source === 'existing' && m.participantKey).map((m) => m.participantKey));
    setPickedUserKeys(Array.from(existing));
    setUsersOpen(true);
  };
  const savePickedUsers = () => {
    const selected = participantOptions.filter((x) => pickedUserKeys.includes(optionKey(x)));
    const existingManual = members.filter((m) => m.source === 'manual');
    const mapped = selected.map((x) => {
      const full = String(x.displayName || '').trim();
      const parts = full.split(/\s+/);
      return {
        id: `u-${optionKey(x)}`,
        source: 'existing',
        participantKey: optionKey(x),
        firstName: parts[0] || full || 'عضو',
        lastName: parts.slice(1).join(' '),
        email: x.subtitle?.includes('@') ? x.subtitle : '',
        phone: '',
        position: 'عضو تنفيذي',
        memberType: 'عضو تنفيذي',
        role: 'عضو',
      };
    });
    setMembers([...existingManual, ...mapped]);
    setUsersOpen(false);
  };
  const toIsoUtc = (date, time) => {
    if (!date || !time) return '';
    const iso = new Date(`${date}T${time}:00`).toISOString();
    return Number.isNaN(new Date(iso).getTime()) ? '' : iso;
  };
  const submitMeeting = async (status) => {
    if (saving) return;
    setSaveMsg('');
    const savedOk = t('meetings.savedOk');
    try {
      setSaving(true);
      const invitees = [
        ...members
          .filter((m) => m.source === 'existing' && m.participantKey)
          .map((m) => {
            const [targetType, targetId] = String(m.participantKey).split(':');
            return {
              targetType: targetType || '',
              targetId: targetId || '',
              displayName: [m.firstName, m.lastName].join(' ').trim() || m.firstName || '',
            };
          }),
        ...members
          .filter((m) => m.source === 'manual' && String(m.email || '').includes('@'))
          .map((m) => ({
            targetType: 'email',
            targetId: String(m.email).trim(),
            displayName: [m.firstName, m.lastName].join(' ').trim() || m.firstName || '',
          })),
      ];
      const payload = mapMeetingFormToPayload({
        companyId: form.companyId,
        committeeId: form.linkCommittee ? form.committeeId : '',
        boardId: form.linkBoard ? form.boardId : '',
        title: form.name,
        goal: form.goal,
        status,
        attendanceType: form.attendanceType,
        location: form.location,
        agenda: null,
        zoomLink: form.meetingLink || null,
        startAtUtc: toIsoUtc(form.date, form.startTime),
        endAtUtc: toIsoUtc(form.date, form.endTime),
        durationMinutes: '',
        notes: null,
        invitees,
        isOpen: form.isOpen,
      });
      if (isEdit && editId && Number.isFinite(editId)) {
        const updated = await updateMeeting(editId, payload);
        setSaveMsg(savedOk);
        openGoogleCalendar(updated);
        navigate(`${basePath}/meetings/${updated.id}`);
        return;
      }
      const created = await createMeeting(payload);
      setSaveMsg(savedOk);
      openGoogleCalendar(created);
      navigate(`${basePath}/meetings/${created.id}`);
    } catch (e) {
      setSaveMsg(e instanceof Error ? e.message : t('meetings.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const fillSampleData = () => {
    const target = new Date();
    target.setDate(target.getDate() + 5);
    if (target.getDay() === 5) target.setDate(target.getDate() + 2);
    if (target.getDay() === 6) target.setDate(target.getDate() + 1);
    const yyyy = target.getFullYear();
    const mm = String(target.getMonth() + 1).padStart(2, '0');
    const dd = String(target.getDate()).padStart(2, '0');
    const sampleDate = `${yyyy}-${mm}-${dd}`;
    const sampleBoardId = filteredBoards[0]?.id ? String(filteredBoards[0].id) : '';
    const sampleCommitteeId = filteredCommittees[0]?.id ? String(filteredCommittees[0].id) : '';

    // Sample content is language-specific (actual data, not UI strings)
    const samples = isRtl ? [
      {
        name: 'اجتماع مجلس الإدارة – مراجعة الميزانية السنوية',
        goal: 'اعتماد ميزانية العام المالي القادم، ومراجعة الإنفاق الفعلي مقارنةً بالمستهدف للعام الحالي، واتخاذ القرارات التصحيحية اللازمة.',
        location: 'المقر الرئيسي، الرياض – قاعة الاجتماعات (A)',
        startTime: '09:30', endTime: '11:00', attendanceType: 'in_person', meetingLink: '',
      },
      {
        name: 'اجتماع اللجنة التنفيذية – متابعة المشاريع الاستراتيجية',
        goal: 'متابعة التقدم في تنفيذ المشاريع الاستراتيجية للربع الثالث، ومراجعة مخاطر التأخير، والموافقة على خطط التعافي.',
        location: 'برج المملكة، الطابق 18 – غرفة الاجتماعات الكبرى',
        startTime: '10:00', endTime: '11:30', attendanceType: 'in_person', meetingLink: '',
      },
      {
        name: 'اجتماع لجنة المراجعة والمخاطر',
        goal: 'مراجعة تقرير المراجع الداخلي للنصف الأول، ومناقشة السياسات المحدّثة لإدارة المخاطر، والتحقق من الالتزام بالمعايير التنظيمية.',
        location: 'عبر الفيديو كونفرنس',
        startTime: '14:00', endTime: '15:30', attendanceType: 'virtual', meetingLink: 'https://meet.google.com/abc-defg-hij',
      },
    ] : [
      {
        name: 'Board of Directors – Annual Budget Review',
        goal: 'Approve the upcoming fiscal year budget, review actual vs. target spend for the current year, and agree on corrective actions where required.',
        location: 'Head Office, Riyadh – Conference Room A',
        startTime: '09:30', endTime: '11:00', attendanceType: 'in_person', meetingLink: '',
      },
      {
        name: 'Executive Committee – Strategic Projects Follow-up',
        goal: 'Review progress on Q3 strategic initiatives, assess delay risks, and approve recovery plans for off-track projects.',
        location: 'Kingdom Tower, Floor 18 – Main Boardroom',
        startTime: '10:00', endTime: '11:30', attendanceType: 'in_person', meetingLink: '',
      },
      {
        name: 'Audit & Risk Committee Meeting',
        goal: 'Review the internal audit report for H1, discuss updated risk management policies, and verify compliance with regulatory standards.',
        location: 'Virtual – Video Conference',
        startTime: '14:00', endTime: '15:30', attendanceType: 'virtual', meetingLink: 'https://meet.google.com/abc-defg-hij',
      },
    ];

    const pick = samples[Math.floor(Math.random() * samples.length)];
    setErrors({});
    setForm((p) => ({
      ...p,
      name: pick.name,
      goal: pick.goal,
      attendanceType: pick.attendanceType,
      location: pick.location,
      meetingLink: pick.meetingLink,
      isOpen: false,
      date: sampleDate,
      startTime: pick.startTime,
      endTime: pick.endTime,
      linkBoard: !!sampleBoardId,
      boardId: sampleBoardId,
      linkCommittee: !!sampleCommitteeId,
      committeeId: sampleCommitteeId,
    }));
  };

  if (accessDenied) return <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>;

  const savedOkMsg = t('meetings.savedOk');

  return (
    <div className="decision-v2" dir={direction} lang={language}>
      <div className="page-wrapper">
        {error && <div className="v2-error">{error}</div>}
        {loading && <div className="v2-loading">{t('common.loading')}</div>}

        {!loading && (
          <div className="card">
            <div className="card-top">
              <div className="top-bar top-bar--single-line">
                <div className="page-title-row">
                  <h1 className="page-title">
                    {isEdit ? (
                      <>
                        <span className="page-title-icon"><Icon name="calendar" size={16} color={ICON_SEC} /></span>
                        {t('meetings.editTitle')}
                      </>
                    ) : (
                      <>
                        {t('meetings.createTitle')}
                        <span className="page-title-icon"><Icon name="calendar" size={16} color={ICON_SEC} /></span>
                      </>
                    )}
                  </h1>
                </div>
                <div className="action-btns">
                  <button type="button" className="btn btn-ghost" onClick={() => navigate(`${basePath}/meetings`)}>{t('common.cancel')}</button>
                  {step === 0 && (
                    <button type="button" className="btn btn-outline" onClick={fillSampleData}>
                      {t('meetings.fillSample')}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="card-inner">
              <div style={{ display: 'flex', flexDirection: 'row', gap: 10, marginBottom: 18, alignItems: 'stretch' }}>
                {steps.map((label, i) => {
                  const done = i < step;
                  const active = i === step;
                  return (
                    <div key={label} style={{ textAlign: 'center', flex: 1, color: active || done ? '#0EA5A0' : '#8AACAC', fontSize: '0.82rem' }}>
                      <button
                        type="button"
                        onClick={() => onStepClick(i)}
                        style={{
                          width: '100%',
                          border: `1.5px solid ${active || done ? '#0EA5A0' : '#DDE6E6'}`,
                          background: active ? '#E0F7F6' : '#fff',
                          padding: '10px 8px',
                          fontWeight: 800,
                          position: 'relative',
                          cursor: 'pointer',
                          color: 'inherit',
                          fontFamily: 'Cairo, sans-serif',
                        }}
                      >
                        <span style={{ display: 'inline-block', minWidth: 20, marginInlineEnd: 6 }}>{done ? '✓' : String(i + 1)}</span>
                        {label}
                        <span style={{ position: 'absolute', right: 0, left: 0, bottom: -1, height: 3, background: active ? '#0EA5A0' : 'transparent' }} />
                      </button>
                    </div>
                  );
                })}
              </div>

              {step === 0 && (
                <div>
                  <div className="fields-grid">
                    <div className="field-group span-2">
                      <label className="field-label">
                        <Icon name="calendar" size={14} color={ICON_SEC} /> {t('meetings.nameField')} <span className="required">*</span>
                      </label>
                      <div className="field-input">
                        <input
                          className={errors.name ? 'mform__input--error' : ''}
                          value={form.name}
                          placeholder={t('meetings.phName')}
                          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                        />
                      </div>
                      {errors.name && <span className="mform__error">{errors.name}</span>}
                    </div>
                    <div className="field-group span-2">
                      <label className="field-label">
                        <Icon name="edit" size={14} color={ICON_SEC} /> {t('meetings.goal')} <span className="required">*</span>
                      </label>
                      <div className="field-input field-input--textarea">
                        <textarea
                          className={errors.goal ? 'mform__input--error' : ''}
                          rows={3}
                          value={form.goal}
                          placeholder={t('meetings.phGoal')}
                          onChange={(e) => setForm((p) => ({ ...p, goal: e.target.value }))}
                        />
                      </div>
                      {errors.goal && <span className="mform__error">{errors.goal}</span>}
                    </div>
                  </div>

                  <div className="section-divider" />

                  <div className="fields-grid">
                    <div className="field-group">
                      <label className="field-label">
                        <Icon name="user" size={14} color={ICON_SEC} /> {t('meetings.attendanceField')} <span className="required">*</span>
                      </label>
                      <div className={`field-input select-wrapper${errors.attendanceType ? ' mform__select--error' : ''}`}>
                        <select value={form.attendanceType} onChange={(e) => setForm((p) => ({ ...p, attendanceType: e.target.value }))}>
                          <option value="">{t('meetings.pickAttendance')}</option>
                          <option value="in_person">{t('meetings.inPerson')}</option>
                          <option value="virtual">{t('meetings.virtual')}</option>
                        </select>
                      </div>
                      {errors.attendanceType && <span className="mform__error">{errors.attendanceType}</span>}
                    </div>
                    <div className="field-group">
                      <label className="field-label">
                        <Icon name="branch" size={14} color={ICON_SEC} /> {t('meetings.place')} <span className="required">*</span>
                      </label>
                      <div className="field-input">
                        <input
                          className={errors.location ? 'mform__input--error' : ''}
                          value={form.location}
                          placeholder={t('meetings.phPlace')}
                          onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
                        />
                      </div>
                      {errors.location && <span className="mform__error">{errors.location}</span>}
                    </div>
                    <div className="field-group">
                      <label className="field-label">
                        <Icon name="link" size={14} color={ICON_SEC} /> {t('meetings.meetingLink')}
                      </label>
                      <div className="field-input">
                        <input
                          dir="ltr"
                          value={form.meetingLink}
                          placeholder={t('meetings.phMeetingLink')}
                          onChange={(e) => setForm((p) => ({ ...p, meetingLink: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="field-group">
                      <label className="field-label" style={{ visibility: 'hidden', marginBottom: 4 }} aria-hidden>—</label>
                      <label className="field-label" style={{ fontWeight: 600 }}>
                        <input type="checkbox" checked={form.isOpen} onChange={(e) => setForm((p) => ({ ...p, isOpen: e.target.checked }))} />
                        اجتماع مفتوح (دخول برابط بدون تسجيل)
                      </label>
                    </div>
                    <div className="field-group">
                      <label className="field-label">
                        <Icon name="calendar" size={14} color={ICON_SEC} /> {t('common.date')} <span className="required">*</span>
                      </label>
                      <div className="field-input">
                        <input type="date" className={errors.date ? 'mform__input--error' : ''} value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} />
                      </div>
                      {errors.date && <span className="mform__error">{errors.date}</span>}
                    </div>
                    <div className="field-group">
                      <label className="field-label">
                        <Icon name="calendar" size={14} color={ICON_SEC} /> {t('meetings.start')} <span className="required">*</span>
                      </label>
                      <div className="field-input">
                        <input type="time" className={errors.startTime ? 'mform__input--error' : ''} value={form.startTime} onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))} />
                      </div>
                      {errors.startTime && <span className="mform__error">{errors.startTime}</span>}
                    </div>
                    <div className="field-group">
                      <label className="field-label">
                        <Icon name="calendar" size={14} color={ICON_SEC} /> {t('meetings.end')} <span className="required">*</span>
                      </label>
                      <div className="field-input">
                        <input type="time" className={errors.endTime ? 'mform__input--error' : ''} value={form.endTime} onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))} />
                      </div>
                      {errors.endTime && <span className="mform__error">{errors.endTime}</span>}
                    </div>
                    <div className="field-group span-3">
                      <label className="field-label">
                        <Icon name="branch" size={14} color={ICON_SEC} /> {t('common.company')}
                      </label>
                      <div className="field-input select-wrapper">
                        <select value={form.companyId} onChange={(e) => setForm((p) => ({ ...p, companyId: e.target.value, boardId: '', committeeId: '' }))}>
                          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="section-divider" />

                  <div className="fields-grid">
                    <div className="field-group span-2">
                      <label className="field-label" style={{ marginBottom: 4 }}>
                        <Icon name="link" size={14} color={ICON_SEC} /> {t('meetings.linkSection')}
                      </label>
                      <label className="field-label" style={{ fontWeight: 600 }}>
                        <input type="checkbox" checked={form.linkBoard} onChange={(e) => setForm((p) => ({ ...p, linkBoard: e.target.checked, boardId: e.target.checked ? p.boardId : '' }))} />
                        {t('meetings.linkBoard')}
                      </label>
                      {form.linkBoard && (
                        <>
                          <div className={`field-input select-wrapper${errors.boardId ? ' mform__select--error' : ''}`}>
                            <select value={form.boardId} onChange={(e) => setForm((p) => ({ ...p, boardId: e.target.value }))}>
                              <option value="">{t('meetings.pickBoard')}</option>
                              {filteredBoards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                          </div>
                          {filteredBoards.length === 0 && <span className="mform__error">{t('meetings.noBoardsCompany')}</span>}
                          {errors.boardId && <span className="mform__error">{errors.boardId}</span>}
                        </>
                      )}
                    </div>
                    <div className="field-group span-2">
                      <label className="field-label" style={{ visibility: 'hidden', marginBottom: 4 }} aria-hidden>—</label>
                      <label className="field-label" style={{ fontWeight: 600 }}>
                        <input type="checkbox" checked={form.linkCommittee} onChange={(e) => setForm((p) => ({ ...p, linkCommittee: e.target.checked, committeeId: e.target.checked ? p.committeeId : '' }))} />
                        {t('meetings.linkCommittee')}
                      </label>
                      {form.linkCommittee && (
                        <>
                          <div className={`field-input select-wrapper${errors.committeeId ? ' mform__select--error' : ''}`}>
                            <select value={form.committeeId} onChange={(e) => setForm((p) => ({ ...p, committeeId: e.target.value }))}>
                              <option value="">{t('meetings.pickCommittee')}</option>
                              {filteredCommittees.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </div>
                          {errors.committeeId && <span className="mform__error">{errors.committeeId}</span>}
                        </>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                    <button type="button" className="btn btn-primary" onClick={() => { if (validate()) setStep(1); }}>
                      {t('meetings.next')}
                    </button>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div>
                  <div className="mform__section-title" style={{ marginBottom: 12 }}>
                    <Icon name="user" size={13} color={ICON_SEC} /> {t('meetings.membersSection')}
                  </div>
                  <div style={{ marginBottom: 12, display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-outline" onClick={openUsersPicker}>{t('meetings.addFromUsers')}</button>
                    <button type="button" className="btn btn-primary" onClick={openMemberModalAdd}>{t('common.add')}</button>
                  </div>
                  <div className="table-card" style={{ border: '1px solid #eef2f5' }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{t('meetings.fullNameCol')}</th>
                          <th>{t('common.email')}</th>
                          <th>{t('meetings.roleField')}</th>
                          <th>{t('meetings.position')}</th>
                          <th>{t('common.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.length === 0 && (
                          <tr><td colSpan={5} style={{ textAlign: 'center', color: '#90a4ae', padding: 18 }}>{t('meetings.noMembersYet')}</td></tr>
                        )}
                        {members.map((m) => {
                          const fullName = `${m.firstName || ''} ${m.lastName || ''}`.trim() || '—';
                          return (
                            <tr key={m.id}>
                              <td>{fullName}</td>
                              <td dir="ltr">{m.email?.trim() ? m.email : '—'}</td>
                              <td>{m.role || 'عضو'}</td>
                              <td>{m.position || '—'}</td>
                              <td>
                                <div className="td-actions" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                  <button type="button" className="action-btn action-btn--edit" title={t('meetings.memberModalDetail')} onClick={() => openMemberModalDetail(m)}>
                                    <Icon name="eye" size={14} />
                                  </button>
                                  <button type="button" className="action-btn action-btn--edit" title={t('meetings.memberModalEdit')} onClick={() => openMemberModalEdit(m)}>
                                    <Icon name="edit" size={14} />
                                  </button>
                                  <button type="button" className="action-btn action-btn--delete" title={t('meetings.removeTitle')} onClick={() => removeMember(m.id)}>
                                    <Icon name="trash" size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                    <button type="button" className="btn btn-outline" onClick={() => setStep(0)}>{t('meetings.prev')}</button>
                    <button type="button" className="btn btn-primary" onClick={() => setStep(2)}>{t('meetings.next')}</button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <div className="mform__section-title" style={{ marginBottom: 12 }}>
                    <Icon name="eye" size={13} color={ICON_SEC} /> {t('meetings.reviewSection')}
                  </div>
                  <div style={{ display: 'grid', gap: 12 }}>
                    <div style={{ border: '1px solid #edf2f7', padding: 12 }}>
                      <div style={{ fontWeight: 800, marginBottom: 8 }}>{t('meetings.summaryBasic')}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 8, color: '#546e7a' }}>
                        <div>{t('meetings.nameField')}: <strong>{form.name || '-'}</strong></div>
                        <div>{t('meetings.goal')}: <strong>{form.goal || '-'}</strong></div>
                        <div>{t('meetings.attendanceField')}: <strong>{form.attendanceType === 'virtual' ? t('meetings.virtual') : t('meetings.inPerson')}</strong></div>
                        <div>{t('meetings.place')}: <strong>{form.location || '-'}</strong></div>
                        <div>{t('common.date')}: <strong>{form.date || '-'}</strong></div>
                        <div>{t('meetings.timeRange')}: <strong>{form.startTime || '-'} - {form.endTime || '-'}</strong></div>
                        {form.meetingLink && (
                          <div style={{ gridColumn: '1 / -1' }}>{t('meetings.meetingLink')}: <strong dir="ltr"><a href={form.meetingLink} target="_blank" rel="noopener noreferrer">{form.meetingLink}</a></strong></div>
                        )}
                      </div>
                    </div>
                    <div style={{ border: '1px solid #edf2f7', padding: 12 }}>
                      <div style={{ fontWeight: 800, marginBottom: 8 }}>{t('meetings.summaryMembers')}</div>
                      <div style={{ color: '#546e7a', marginBottom: 6 }}>{t('meetings.membersCount')}: <strong>{members.length}</strong></div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        {members.slice(0, 8).map((m) => (
                          <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #eceff1', paddingBottom: 4 }}>
                            <span>{[m.firstName, m.lastName].join(' ').trim() || '-'}</span>
                            <span style={{ color: '#78909c' }}>{m.role || 'عضو'}</span>
                          </div>
                        ))}
                        {members.length === 0 && <div style={{ color: '#90a4ae' }}>{t('meetings.noMembersYet')}</div>}
                      </div>
                    </div>
                  </div>
                  {saveMsg && (
                    <div style={{ marginTop: 12, padding: '10px 12px', background: saveMsg === savedOkMsg ? '#eef7ff' : '#ffebee', color: saveMsg === savedOkMsg ? '#0d47a1' : '#c62828' }}>
                      {saveMsg}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                    <button type="button" className="btn btn-outline" onClick={() => setStep(1)}>{t('meetings.prev')}</button>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" className="btn btn-ghost" onClick={() => submitMeeting('draft')} disabled={saving}>
                        {saving ? t('meetings.saving') : t('meetings.saveDraft')}
                      </button>
                      <button type="button" className="btn btn-primary" onClick={() => submitMeeting('published')} disabled={saving}>
                        {saving ? t('meetings.saving') : t('meetings.publish')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Modal
        open={memberModalOpen}
        onClose={closeMemberModal}
        title={memberModalMode === 'add' ? t('meetings.memberModalAdd') : memberModalMode === 'edit' ? t('meetings.memberModalEdit') : t('meetings.memberModalDetail')}
        subtitle=""
        size="lg"
      >
        <div className="mform member-modal-mform">
          <div className="mform__field">
            <label className="mform__label"><Icon name="user" size={14} color={ICON_SEC} /> {t('meetings.firstName')} <span>*</span></label>
            <div className="mform__input-wrap">
              <Icon name="user" size={14} color={ICON_SEC} className="mform__input-icon" />
              <input
                className={`mform__input mform__input--with-icon${memberModalErrors.firstName ? ' mform__input--error' : ''}`}
                disabled={memberModalMode === 'detail'}
                placeholder={t('meetings.phMemFirst')}
                value={memberModalForm.firstName}
                onChange={(e) => setMemberModalForm((p) => ({ ...p, firstName: e.target.value }))}
              />
            </div>
            {memberModalErrors.firstName && <span className="mform__error">{memberModalErrors.firstName}</span>}
          </div>
          <div className="mform__field">
            <label className="mform__label"><Icon name="user" size={14} color={ICON_SEC} /> {t('meetings.lastName')} <span>*</span></label>
            <div className="mform__input-wrap">
              <Icon name="user" size={14} color={ICON_SEC} className="mform__input-icon" />
              <input
                className={`mform__input mform__input--with-icon${memberModalErrors.lastName ? ' mform__input--error' : ''}`}
                disabled={memberModalMode === 'detail'}
                placeholder={t('meetings.phMemLast')}
                value={memberModalForm.lastName}
                onChange={(e) => setMemberModalForm((p) => ({ ...p, lastName: e.target.value }))}
              />
            </div>
            {memberModalErrors.lastName && <span className="mform__error">{memberModalErrors.lastName}</span>}
          </div>
          <div className="mform__field">
            <label className="mform__label"><Icon name="decisions" size={14} color={ICON_SEC} /> {t('common.email')}</label>
            <div className="mform__input-wrap">
              <Icon name="decisions" size={14} color={ICON_SEC} className="mform__input-icon" />
              <input
                className="mform__input mform__input--with-icon"
                disabled={memberModalMode === 'detail'}
                dir="ltr"
                placeholder={t('meetings.phMemEmail')}
                value={memberModalForm.email}
                onChange={(e) => setMemberModalForm((p) => ({ ...p, email: e.target.value }))}
              />
            </div>
          </div>
          <div className="mform__field">
            <label className="mform__label"><Icon name="chat" size={14} color={ICON_SEC} /> {t('common.phone')}</label>
            <div className="mform__input-wrap">
              <Icon name="chat" size={14} color={ICON_SEC} className="mform__input-icon" />
              <input
                className="mform__input mform__input--with-icon"
                disabled={memberModalMode === 'detail'}
                dir="ltr"
                placeholder={t('meetings.phMemPhone')}
                value={memberModalForm.phone}
                onChange={(e) => setMemberModalForm((p) => ({ ...p, phone: e.target.value }))}
              />
            </div>
          </div>
          <div className="mform__field">
            <label className="mform__label"><Icon name="board" size={14} color={ICON_SEC} /> {t('meetings.position')}</label>
            <div className="mform__input-wrap">
              <Icon name="board" size={14} color={ICON_SEC} className="mform__input-icon" />
              <input
                className="mform__input mform__input--with-icon"
                disabled={memberModalMode === 'detail'}
                placeholder={t('meetings.phMemPosition')}
                value={memberModalForm.position}
                onChange={(e) => setMemberModalForm((p) => ({ ...p, position: e.target.value }))}
              />
            </div>
          </div>
          <div className="mform__field">
            <label className="mform__label"><Icon name="filter" size={14} color={ICON_SEC} /> {t('meetings.memberType')}</label>
            <div className="mform__input-wrap">
              <Icon name="filter" size={14} color={ICON_SEC} className="mform__input-icon" />
              <input
                className="mform__input mform__input--with-icon"
                disabled={memberModalMode === 'detail'}
                placeholder={t('meetings.phMemMemberType')}
                value={memberModalForm.memberType}
                onChange={(e) => setMemberModalForm((p) => ({ ...p, memberType: e.target.value }))}
              />
            </div>
          </div>
          <div className="mform__field">
            <label className="mform__label"><Icon name="settings" size={14} color={ICON_SEC} /> {t('meetings.roleField')}</label>
            <div className="mform__input-wrap">
              <Icon name="settings" size={14} color={ICON_SEC} className="mform__input-icon" />
              <select
                className="mform__select mform__input--with-icon"
                disabled={memberModalMode === 'detail'}
                value={memberModalForm.role}
                onChange={(e) => setMemberModalForm((p) => ({ ...p, role: e.target.value }))}
              >
                {memberModalForm.role && !MEETING_MEMBER_ROLES.includes(memberModalForm.role) && (
                  <option value={memberModalForm.role}>{memberModalForm.role}</option>
                )}
                {MEETING_MEMBER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          {memberModalSource === 'existing' && memberModalMode === 'detail' && (
            <p style={{ margin: '8px 0 0', color: '#607d8b', fontSize: '0.85rem' }}>{t('meetings.existingMemberNote')}</p>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-ghost" onClick={closeMemberModal}>
            {memberModalMode === 'detail' ? t('common.close') : t('common.cancel')}
          </button>
          {memberModalMode !== 'detail' && (
            <button type="button" className="btn btn-primary" onClick={saveMeetingMemberModal}>{t('common.save')}</button>
          )}
        </div>
      </Modal>

      <Modal open={usersOpen} onClose={() => setUsersOpen(false)} title={t('meetings.usersModal')} subtitle={t('meetings.usersModalSub')} size="md">
        <div style={{ display: 'grid', gap: 8, maxHeight: 380, overflow: 'auto' }}>
          {participantOptions.map((u) => (
            <label key={optionKey(u)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8, border: '1px solid #eceff1' }}>
              <input
                type="checkbox"
                checked={pickedUserKeys.includes(optionKey(u))}
                onChange={(e) => setPickedUserKeys((p) => (e.target.checked ? [...p, optionKey(u)] : p.filter((x) => x !== optionKey(u))))}
              />
              <span style={{ fontWeight: 700 }}>{u.displayName}</span>
              {u.subtitle && <span dir="ltr" style={{ color: '#607d8b' }}>{u.subtitle}</span>}
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button type="button" className="btn btn-ghost" onClick={() => setUsersOpen(false)}>{t('common.cancel')}</button>
          <button type="button" className="btn btn-primary" onClick={savePickedUsers}>{t('common.add')}</button>
        </div>
      </Modal>
    </div>
  );
}
